import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { ErrorAlert } from '../..';
import titleCase from '../../../../../shared/utils/titleCase';
import Button, { ButtonGroup } from '../../Button';
import type { AsyncStatus } from '../../../../types';
import type { ResourceType } from '../types';

type Props = {
  resourceType: ResourceType;
  resourceName?: string;
  isDisplayed: boolean;
  isDeleting: boolean;
  errors?: AsyncStatus['errors'];
  onDelete: () => void;
  onHide: () => void;
};

/**
 * DeleteModal is a warning modal which is displayed before the user deletes a saved resource.
 */
const DeleteModal: VoidFunctionComponent<Props> = ({
  resourceType,
  resourceName,
  isDisplayed,
  isDeleting,
  errors,
  onDelete,
  onHide,
}) => (
  <Modal
    animation
    show={isDisplayed}
    onHide={onHide}
    onEscapeKeyDown={onHide}
  >
    <Modal.Header>
      <Modal.Title>
        Are you sure you want to delete this {resourceType}?
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div>
        {resourceName && (<>You are about to delete <strong>{resourceName}</strong>?{' '}</>)}
        {`This change could adversely affect other users that depend on this ${resourceType} in their projects.`}
      </div>
      {errors && errors.length > 0 && (
        <div className="margin-top-md">
          <ErrorAlert status={{ errors }} />
        </div>
      )}
    </Modal.Body>
    <Modal.Footer>
      <ButtonGroup>
        <Button
          type="secondary"
          size="medium"
          onClick={onHide}
          isDisabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          type="error"
          size="medium"
          onClick={onDelete}
          isDisabled={isDeleting}
          testId={`saved-${resourceType}-modal-submit--delete`}
        >
          Delete {titleCase(resourceType)}
        </Button>
      </ButtonGroup>
    </Modal.Footer>
  </Modal>
);

export default DeleteModal;
